<template>
  <div>
    <div class="sticky-headers">
      <mf-tabs-menu
        v-model="offersTypeHighLevelTabProp"
        :highlight-color="highlightColor"
        :tabs="offersTypeTabsHighLevel"
        :tabs-errors="offersTypeTabsWithErrorsHighLevel"
        class="mt-4"
      >
        <template v-slot:children>
          <mf-tabs-menu
            v-if="getSubTabTabs.length > 0"
            v-model="offersTypeLowLevelTabProp"
            :tabs="getSubTabTabs"
            :tabs-errors="getOffersTypeTabsWithErrorsLowLevel"
            class="mt-4"
          />
          <div v-else class="empty"></div>
        </template>
      </mf-tabs-menu>
    </div>

    <cashback-expiration-date
      v-if="showCashbackExpiration"
      :type.sync="expirationType"
      :expiration-in-days.sync="expirationInDaysComputed"
      :expire-date.sync="expireDateComputed"
      :end-date="campaign.end_date"
      :disabled="disableCashbackExpiration"
      :input-error-message="cashbackExpirationErrorMessage"
    />
    <div v-if="offerSalesKeyName !== 'combo_offer_sales' && !hideToolbar" class="d-flex align-center justify-end py-6 pr-2 sticky-headers-actions">
      <mf-button
        v-if="!isIndustryCampaign && !hideButtons"
        :disabled="
          (campaign.segments && campaign.segments.length > 0 && campaign.status == 'active') ||
          automaticCampaign ||
          campaign.status === 'processing' ||
          (isIntelligentDistributionEnabled && ['active', 'scheduled'].includes(campaign.status))
        "
        label=" Importar ofertas"
        @click="openImportModal = true"
      />

      <import-dynamic-csv
        v-if="!isIndustryCampaign && !hideButtons"
        v-model="openImportModal"
        title="Importar ofertas"
        import-label="Importar lista de ofertas"
        load-objects
        :required-any-of="['sku', 'ean']"
        :fields="importFields"
        is-offer-sales
      />
      <modal-graphql-search-content
        v-if="!isIndustryCampaign && !hideButtons"
        :campaign="campaign"
        :offer-type="offerSalesKeyName"
        button-activator
        hide-csv-upload
        return-groups
        only-active
        :disabled="
          automaticCampaign || campaign.status === 'processing' || (isIntelligentDistributionEnabled && ['active', 'scheduled'].includes(campaign.status))
        "
        button-text="Escolher produtos ou grupos"
        :selected-items.sync="campaign[offerSalesKeyName]"
        :is-segmentation="campaign.segments && campaign.segments.length > 0 && campaign.status == 'active'"
        @confirm="insertProducts"
      />
      <mf-search v-model="searchProp" dense hide-details placeholder="Buscar produto" autofocus />
    </div>
  </div>
</template>

<script>
import { QUERY_VALIDATE_IMPORTED_PRODUCTS } from '@/modules/campanhas/gestao-ofertas-v2/graphql'
import flatten from 'lodash/flatten'
import cloneDeep from 'lodash/cloneDeep'
import findIndex from 'lodash/findIndex'
import { parseLocaleFloat } from '@/config/helpers.js'
import intelligentDistributionMixin from '../mixins/intelligent-distribution.js'

export default {
  name: 'OfferSalesTableHeaders',
  components: {
    ModalGraphqlSearchContent: () => import('@/components/ModalGraphqlSearchContent.vue'),
    ImportDynamicCsv: () => import('@/components/ImportDynamicCsv.vue'),
    CashbackExpirationDate: () => import('./CashbackExpirationDate.vue'),
  },
  mixins: [intelligentDistributionMixin],

  props: {
    campaign: {
      type: Object,
      default: () => {},
    },
    offersTypeTabHighLevel: {
      type: Number,
      default: 0,
    },
    offersTypeTabsHighLevel: {
      type: Array,
      default: () => [],
    },
    offersTypeTabLowLevel: {
      type: Number,
      default: 0,
    },
    offersTypeTabsLowLevel: {
      type: Array,
      default: () => [],
    },
    offersTypeTabsWithErrorsLowLevel: {
      type: Array,
      default: () => [],
    },
    search: {
      type: String,
      default: '',
    },
    automaticCampaign: {
      type: Boolean,
      default: false,
    },
    isIndustryCampaign: {
      type: Boolean,
      default: false,
    },
    hideButtons: {
      type: Boolean,
      default: false,
    },
    expirationInDays: {
      type: Number,
      default: 1,
    },
    expireDate: {
      type: String,
      default: '',
    },
    cashbackExpirationErrorMessage: {
      type: String,
      default: '',
    },
    cashbackExpirationType: {
      type: String,
      default: 'AT_PURCHASE',
    },
    hideToolbar: {
      type: Boolean,
      default: false,
    },
    highlightColor: {
      type: String,
      default: '',
    },
  },

  provide: function () {
    return {
      objectSelected: this.formatImportedOffers,
      updateRequiredColumns: () => {},
    }
  },

  data: () => ({
    openImportModal: false,
  }),

  computed: {
    disableCashbackExpiration() {
      return !(this.campaign.cashback_percentage_offer_sales.length > 0 || this.campaign.cashback_absolute_offer_sales.length > 0)
    },
    expirationType: {
      get() {
        return this.cashbackExpirationType
      },
      set(value) {
        this.$emit('update:cashbackExpirationType', value)
      },
    },
    expirationInDaysComputed: {
      get() {
        return this.expirationInDays
      },
      set(value) {
        this.$emit('update:expirationInDays', value)
      },
    },
    expireDateComputed: {
      get() {
        return this.expireDate
      },
      set(value) {
        this.$emit('update:expireDate', value)
      },
    },
    isAdvancedOffer() {
      return this.$getAllowedModules()['advanced_discounts']
    },
    isCashbackPercentage() {
      return this.$getAllowedModules()['cashback']
    },
    isCashbackAbsolute() {
      return this.$getAllowedModules()['cashback']
    },
    isPoints() {
      return this.$getAllowedModules()['points']
    },
    showCashbackExpiration() {
      return (
        (this.isCashbackAbsolute || this.isCashbackPercentage) &&
        this.offersTypeHighLevelTabProp === 1 &&
        [0, 1].includes(this.offersTypeLowLevelTabProp) &&
        !this.isIndustryCampaign
      )
    },
    getSubTabTabs() {
      return this.offersTypeTabsLowLevel.filter((tab) => tab.highLevel === this.offersTypeTabHighLevel)
    },
    offersTypeTabsWithErrorsHighLevel() {
      const tabsWithErrors = []
      const hasOffersTabsErrors = this.offersTypeTabsWithErrorsLowLevel.some((tab) => tab <= 3)
      const hasCashbackTabsErrors = this.offersTypeTabsWithErrorsLowLevel.some((tab) => tab >= 4)
      if (hasOffersTabsErrors) tabsWithErrors.push(0)
      if (hasCashbackTabsErrors) tabsWithErrors.push(1)
      return tabsWithErrors
    },
    getOffersTypeTabsWithErrorsLowLevel() {
      const tabsWithErrors = []
      if (!this.offersTypeTabsWithErrorsLowLevel.length) return []

      this.offersTypeTabsWithErrorsLowLevel.forEach((tab) => {
        // tabs from offers are from 0 to 3 and cashback and points are from 4 to 6
        if (tab <= 3 && this.offersTypeTabHighLevel === 0) tabsWithErrors.push(tab)
        else if (tab >= 4 && this.offersTypeTabHighLevel === 1) tabsWithErrors.push(tab - 4)
      })

      return tabsWithErrors
    },
    searchProp: {
      get() {
        return this.search
      },
      set(value) {
        this.$emit('update:search', value)
      },
    },
    offersTypeHighLevelTabProp: {
      get() {
        return this.offersTypeTabHighLevel
      },
      set(value) {
        this.$emit('update:offersTypeTabLowLevel', 0)
        this.$emit('update:offersTypeTabHighLevel', value)
      },
    },
    offersTypeLowLevelTabProp: {
      get() {
        return this.offersTypeTabLowLevel
      },
      set(value) {
        this.$emit('update:offersTypeTabLowLevel', value)
      },
    },
    offerSalesKeyName() {
      return this.offersTypeHighLevelTabProp === 0
        ? this.offersAndDiscounts[this.offersTypeLowLevelTabProp]
        : this.cashbackAndPoints[this.offersTypeLowLevelTabProp]
    },
    isOffersByUnitPermission() {
      return this.$getAllowedModules()['offers_by_unit']
    },
    offersAndDiscounts() {
      let modulesActive = ['offer_sales']
      const advancedOffers = ['percentage_offer_sales', 'takepay_offer_sales', 'combo_offer_sales']
      if (this.isOffersByUnitPermission) advancedOffers.splice(1, 0, 'dynamic_pricing_percentage_offer_sales')
      if (this.isAdvancedOffer) modulesActive = modulesActive.concat(advancedOffers)
      return modulesActive
    },
    cashbackAndPoints() {
      const modulesActive = []
      if (this.isCashbackAbsolute) modulesActive.push('cashback_percentage_offer_sales', 'cashback_absolute_offer_sales')
      if (this.isPoints) modulesActive.push('points_offer_sales')
      return modulesActive
    },
    productsExtraFieldsInfo() {
      let info = {}
      if (this.offerSalesKeyName === 'offer_sales') {
        info = {
          price_1: 0,
          price_2: 0,
          origin: 'search',
          maximum_per_client: 0,
          minimum_per_client: 0,
          original_eans: [],
        }
      } else if (this.offerSalesKeyName === 'percentage_offer_sales') {
        info = {
          percentage_discount: 0,
          origin: 'search',
          maximum_per_client: 0,
          minimum_per_client: 0,
          original_eans: [],
        }
      } else if (this.offerSalesKeyName === 'dynamic_pricing_percentage_offer_sales') {
        info = {
          percentage_discount: 0,
          origin: 'search',
          maximum_per_client: 0,
          minimum_per_client: 0,
          original_eans: [],
        }
      } else if (this.offerSalesKeyName === 'takepay_offer_sales') {
        info = {
          take: 0,
          pay: 0,
          origin: 'search',
          maximum_per_client: 0,
          original_eans: [],
        }
      } else if (this.offerSalesKeyName === 'cashback_percentage_offer_sales') {
        info = {
          price_1: 0,
          cashback_percentage: 0,
          origin: 'search',
          maximum_per_client: 0,
          minimum_per_client: 0,
          original_eans: [],
        }
      } else if (this.offerSalesKeyName === 'cashback_absolute_offer_sales') {
        info = {
          price: 0,
          cashback_absolute: 0,
          origin: 'search',
          maximum_per_client: 0,
          minimum_per_client: 0,
          original_eans: [],
        }
      } else if (this.offerSalesKeyName === 'points_offer_sales') {
        info = {
          price: 0,
          additional_points_rate: 0,
          origin: 'search',
          maximum_per_client: 0,
          minimum_per_client: 0,
          original_eans: [],
        }
      }

      return info
    },
    importFields() {
      let csvFields = []
      if (this.offerSalesKeyName === 'offer_sales') {
        csvFields = [
          { text: 'SKU', value: 'sku' },
          { text: 'EAN', value: 'ean' },
          { text: 'Descrição', value: 'name' },
          { text: 'Preço 1', value: 'price_1' },
          { text: 'Preço 2', value: 'price_2' },
          { text: 'Mínimo por cliente', value: 'minimum_per_client' },
          { text: 'Máximo por cliente', value: 'maximum_per_client' },
        ]
      } else if (this.offerSalesKeyName === 'percentage_offer_sales') {
        csvFields = [
          { text: 'SKU', value: 'sku' },
          { text: 'EAN', value: 'ean' },
          { text: 'Descrição', value: 'name' },
          { text: 'Desconto', value: 'percentage_discount' },
          { text: 'Mínimo por cliente', value: 'minimum_per_client' },
          { text: 'Máximo por cliente', value: 'maximum_per_client' },
        ]
      } else if (this.offerSalesKeyName === 'dynamic_pricing_percentage_offer_sales') {
        csvFields = [
          { text: 'SKU', value: 'sku' },
          { text: 'EAN', value: 'ean' },
          { text: 'Descrição', value: 'name' },
          { text: 'Desconto', value: 'percentage_discount' },
          { text: 'Mínimo por cliente', value: 'minimum_per_client' },
          { text: 'Máximo por cliente', value: 'maximum_per_client' },
        ]
      } else if (this.offerSalesKeyName === 'takepay_offer_sales') {
        csvFields = [
          { text: 'SKU', value: 'sku' },
          { text: 'EAN', value: 'ean' },
          { text: 'Descrição', value: 'name' },
          { text: 'Leve', value: 'take' },
          { text: 'Pague', value: 'pay' },
          { text: 'Max grupos p/ CPF', value: 'maximum_per_client' },
        ]
      } else if (this.offerSalesKeyName === 'cashback_percentage_offer_sales' || this.offerSalesKeyName === 'cashback_absolute_offer_sales') {
        const cashback = this.offerSalesKeyName === 'cashback_percentage_offer_sales' ? 'cashback_percentage' : 'cashback_absolute'
        const cashbackText = this.offerSalesKeyName === 'cashback_percentage_offer_sales' ? 'Cashback (%)' : 'Cashback (R$)'
        const price = this.offerSalesKeyName === 'cashback_percentage_offer_sales' ? 'price_1' : 'price'
        csvFields = [
          { text: 'SKU', value: 'sku' },
          { text: 'EAN', value: 'ean' },
          { text: 'Descrição', value: 'name' },
          { text: 'Preço 1', value: price },
          { text: cashbackText, value: cashback },
          { text: 'Mínimo por cliente', value: 'minimum_per_client' },
          { text: 'Máximo por cliente', value: 'maximum_per_client' },
        ]
      } else if (this.offerSalesKeyName === 'points_offer_sales') {
        csvFields = [
          { text: 'SKU', value: 'sku' },
          { text: 'EAN', value: 'ean' },
          { text: 'Descrição', value: 'name' },
          { text: 'Preço 1', value: 'price' },
          { text: 'Pontos', value: 'additional_points_rate' },
          { text: 'Mínimo por cliente', value: 'minimum_per_client' },
          { text: 'Máximo por cliente', value: 'maximum_per_client' },
        ]
      }
      return csvFields
    },
  },

  mounted() {
    if (this.campaign) {
      this.insertProducts(this.campaign.offer_sales, 'offer_sales')
      this.insertProducts(this.campaign.percentage_offer_sales, 'percentage_offer_sales')
      this.insertProducts(this.campaign.dynamic_pricing_percentage_offer_sales, 'dynamic_pricing_percentage_offer_sales')
      this.insertProducts(this.campaign.takepay_offer_sales, 'takepay_offer_sales')
      this.insertProducts(this.campaign.cashback_percentage_offer_sales, 'cashback_percentage_offer_sales')
      this.insertProducts(this.campaign.cashback_absolute_offer_sales, 'cashback_absolute_offer_sales')
      this.insertProducts(this.campaign.points_offer_sales, 'points_offer_sales')
    }
  },

  methods: {
    formatDefaultIntValue(value) {
      if (!value) return 0
      return parseInt(value.replace(',', '.'))
    },

    getExtraFieldsForFormatImportedOffers(product) {
      let info = {}
      if (this.offerSalesKeyName === 'offer_sales') {
        info = {
          price_1: parseLocaleFloat(product.price_1),
          price_2: parseLocaleFloat(product.price_2),
          minimum_per_client: parseLocaleFloat(product.minimum_per_client),
          maximum_per_client: parseLocaleFloat(product.maximum_per_client),
        }
      } else if (this.offerSalesKeyName === 'percentage_offer_sales' || this.offerSalesKeyName === 'dynamic_pricing_percentage_offer_sales') {
        info = {
          percentage_discount: this.formatDefaultIntValue(product.percentage_discount),
          minimum_per_client: parseLocaleFloat(product.minimum_per_client),
          maximum_per_client: parseLocaleFloat(product.maximum_per_client),
        }
      } else if (this.offerSalesKeyName === 'takepay_offer_sales') {
        info = {
          take: parseLocaleFloat(product.take),
          pay: parseLocaleFloat(product.pay),
          maximum_per_client: parseLocaleFloat(product.maximum_per_client),
        }
      } else if (this.offerSalesKeyName === 'cashback_percentage_offer_sales') {
        info = {
          price_1: parseLocaleFloat(product.price_1),
          cashback_percentage: this.formatDefaultIntValue(product.cashback_percentage),
          minimum_per_client: parseLocaleFloat(product.minimum_per_client),
          maximum_per_client: parseLocaleFloat(product.maximum_per_client),
        }
      } else if (this.offerSalesKeyName === 'cashback_absolute_offer_sales') {
        info = {
          price: parseLocaleFloat(product.price),
          cashback_absolute: parseLocaleFloat(product.cashback_absolute),
          minimum_per_client: parseLocaleFloat(product.minimum_per_client),
          maximum_per_client: parseLocaleFloat(product.maximum_per_client),
        }
      } else if (this.offerSalesKeyName === 'points_offer_sales') {
        info = {
          price: parseLocaleFloat(product.price),
          additional_points_rate: this.formatDefaultIntValue(product.additional_points_rate),
          minimum_per_client: parseLocaleFloat(product.minimum_per_client),
          maximum_per_client: parseLocaleFloat(product.maximum_per_client),
        }
      }
      return info
    },

    async formatImportedOffers({ objects, selectedColumns }) {
      let idField = null
      let ids = []

      if (selectedColumns.includes('sku')) {
        idField = 'sku'
        ids = objects.map(({ sku }) => sku)
      } else if (selectedColumns.includes('ean')) {
        idField = 'ean'
        ids = objects.map(({ ean }) => ean)
      } else {
        this.$alert({
          title: 'Erro na importação',
          message: 'O arquivo importado precisa conter SKU ou EAN',
        })
        return
      }

      ids = ids.filter((item) => item !== '')

      const limitedIds = ids.slice(0, 1500)
      const excessProducts = ids.slice(1500)

      const payload = { active: true, page_size: 1500 }
      if (this.campaign.units) payload.units = this.campaign.units
      payload[`${idField}s`] = limitedIds

      await this.$apollo
        .query({
          query: QUERY_VALIDATE_IMPORTED_PRODUCTS,
          variables: payload,
        })
        .then((response) => {
          this.productsFound = response.data.products.products.map((prod) => cloneDeep(prod))
          let allValues = []
          this.productsFound.forEach((prod) => {
            allValues = [...allValues, ...flatten(prod.eans), prod.ean, prod.sku, prod.image]
          })
          const notFoundFromLimited = limitedIds.filter((item) => allValues.indexOf(item) === -1)
          this.productsNotFound = [...notFoundFromLimited, ...excessProducts]
        })
      let confirmed = true

      if (this.productsNotFound.length) {
        const excessCount = excessProducts.length
        const notFoundCount = this.productsNotFound.length - excessCount
        const notFoundMessage =
          notFoundCount > 0
            ? `\n\n${notFoundCount} produto(s) não foram encontrados ou estão inválidos:\n${this.productsNotFound.slice(0, notFoundCount).join('\n')}`
            : ''
        const excessMessage = excessCount > 0 ? `\n\n${excessCount} produto(s) excederam o limite de 1500 produtos:\n${excessProducts.join('\n')}` : ''

        confirmed = await this.$confirm({
          message: `Importamos ${this.productsFound.length} produtos com sucesso.${notFoundMessage}${excessMessage}\n\nDeseja continuar a importação?`,
          confirmText: 'Continuar',
          cancelText: 'Cancelar',
        })
          .then(() => true)
          .catch(() => false)
      }

      if (!confirmed) return

      const checkedProducts = this.productsFound.reduce((acc, product) => {
        const importedEansForProduct = []

        // Update object's eans and find all eans for a given product
        objects.forEach((object) => {
          if (idField === 'ean' && product.eans.includes(object.ean)) {
            object.ean = product.ean
          }

          if (object[idField] === product[idField] && !!object.ean) {
            importedEansForProduct.push(object.ean)
          }
        })

        // Ensure product eans is exists
        if (!product.eans.length) {
          product.eans = [product.ean]
        }

        // Add imported eans into product eans, ensure no duplicates are added

        importedEansForProduct.forEach((ean) => {
          if (!product.eans.includes(ean)) {
            product.eans.push(ean)
          }
        })

        return { ...acc, [product[idField]]: product }
      }, {})

      const productsToInsert = []
      const insertedIds = []

      objects.forEach((product, index) => {
        if (checkedProducts[product[idField]]) {
          const item = checkedProducts[product[idField]]
          productsToInsert.push({
            _id: item._id,
            imported: true,
            active: true,
            name: product.name || item.name,
            sku: item.sku,
            ean: item.ean,
            eans: item.eans,
            original_eans: [],
            image_url: item.image,
            units_with_price_1: item?.units_with_price_1,
            product: {
              ean: item.ean,
              eans: item.eans,
              image: item.image,
            },
            ...this.getExtraFieldsForFormatImportedOffers(product),
          })
        }
        insertedIds.push(product[idField])
      })

      const clonedProducts = cloneDeep(this.campaign[this.offerSalesKeyName])

      const checkIfExists = (prev, curr) => (prev.sku && prev.sku === curr.sku) || (prev._id && prev._id === curr._id)

      const allProducts = clonedProducts
        .concat(productsToInsert)
        .reduce((products, current) => (products.some((prev) => checkIfExists(prev, current)) ? products : [...products, current]), [])

      this.$emit('insert-products', { offerSalesKeyName: this.offerSalesKeyName, products: allProducts })
    },

    insertProducts(groupProducts, offerSalesName = '') {
      if (!groupProducts) return

      const offerSalesKeyName = offerSalesName || this.offerSalesKeyName
      const clonedProducts = []

      groupProducts.forEach((product, index) => {
        const productIndex = findIndex(this.campaign[offerSalesKeyName], (p) => (p._id && p._id === product._id) || (p.sku && p.sku === product.sku))

        if (productIndex >= 0) {
          let newProduct = {}
          if (product.products) {
            const { products, imported, ...groupWithoutProducts } = this.campaign[offerSalesKeyName][productIndex]
            newProduct = Object.assign(this.formatProduct(product, index, imported), groupWithoutProducts)
          } else {
            const isImported = false
            newProduct = Object.assign(this.formatProduct(product, index, isImported), this.campaign[offerSalesKeyName][productIndex])
          }
          clonedProducts.push(newProduct)
        } else {
          const isImported = offerSalesKeyName !== 'combo_offer_sales'
          const baseProduct = this.formatProduct(product, index, isImported)
          clonedProducts.push(
            offerSalesKeyName !== 'combo_offer_sales'
              ? baseProduct
              : Object.assign(baseProduct, { quantity: product.quantity || this.productsExtraFieldsInfo.quantity })
          )
        }
      })

      this.$emit('insert-products', { offerSalesKeyName, products: clonedProducts })
    },

    formatProduct(product, index, isImported) {
      if (product.target_products && product.trigger_products) return product

      if (product.products) {
        const formatted = product.products.map((item) => ({
          isGroup: true,
          imported: isImported,
          disabled: this.isDisabledProduct(isImported),
          active: typeof item.active === 'boolean' ? item.active : true,
          sku: item.sku,
          ean: item.ean,
          _id: item._id,
          eans: item.eans.length === 1 ? [item.ean] : item.eans,
          name: item.name,
          origin: 'search',
          original_eans: item.product?.eans || [],
          image_url: item.image_url || item.image,
          units_with_price_1: item.units_with_price_1,
          product: item.product || {
            image_url: item.image,
            ean: item.ean,
            eans: item.eans.length > 1 ? item.eans : [item.ean],
          },
          index,
          sync_data: item?.sync_data,
        }))

        return Object.assign(
          {
            _id: product._id,
            isGroup: true,
            imported: isImported,
            disabled: this.isDisabledProduct(isImported),
            name: product.name,
            products: formatted,
            group_rules_exhibition: this.offerSalesKeyName === 'dynamic_pricing_percentage_offer_sales' ? 'PRIVATE' : product.rules_exhibition || 'PRIVATE',
            group_image_url: product.image_url,
            categories_final_consumer: product.categories_final_consumer,
            index,
          },
          this.productsExtraFieldsInfo
        )
      }

      return Object.assign(
        {
          _id: product._id,
          imported: isImported,
          disabled: this.isDisabledProduct(isImported),
          active: typeof product.active === 'boolean' ? product.active : true,
          sku: product.sku,
          ean: product.ean,
          eans: product?.eans?.length === 1 ? [product.ean] : product.eans,
          name: product.name,
          image_url: product.image_url || product.image,
          units_with_price_1: product.units_with_price_1,
          product: {
            image: product.image,
            ean: product.ean,
            eans: product.eans.length > 1 ? product.eans : [product.ean],
          },
          index,
        },
        this.productsExtraFieldsInfo
      )
    },

    isDisabledProduct(isImported) {
      return (
        this.automaticCampaign ||
        this.campaign.status === 'finished' ||
        this.campaign.status === 'expired' ||
        this.campaign.status === 'industry_rejected' ||
        this.campaign.status === 'processing' ||
        (['active', 'scheduled'].includes(this.campaign.status) && isImported !== true)
      )
    },
  },
}
</script>

<style lang="scss" scoped>
.sticky-headers {
  position: -webkit-sticky;
  position: sticky;
  top: 64px;
  z-index: 2;
  background-color: $cinza-l90;
}

.v-input {
  max-width: 275px;
}

.sticky-headers-actions {
  gap: 20px;
}
</style>
